import React, { createContext, ReactNode, useContext, useRef, useState } from "react";

type T_Props = {
    children: ReactNode
}

interface I_FormData {
    [key: string]: any;
}

interface I_FormContextType {
    ctxFormData: I_FormData;
    ctxFormSchema?: I_FormData;
    updateCtxFormSchema: ({ formName, schema }: { formName: string, schema?: any }) => void;
    updateCtxFormData: ({ formName, name, value, data }: { formName: string, name?: string, value?: string, data?: any }) => void;
    resetFormData: () => void;
    focusedInput: string | null;
    setFocusedInput: (name: string | null) => void;
    inputsRef: React.MutableRefObject<Record<string, any | null | undefined>>;
}

const FormContext = createContext<I_FormContextType>({
    ctxFormData: {},
    ctxFormSchema: {},
    updateCtxFormSchema: function ({ formName, schema }: {formName: string, schema?: any}): void {
        // console.log("updateCtxFormSchema ---- ", name, value, new Error("Function not implemented."));
        // throw new Error("Function not implemented.");
    },
    updateCtxFormData: function ({ formName, name, value, data }: { formName: string, name?: string, value?: string, data?: any }): void {
        // console.log("updateCtxFormData ---- ", name, value, new Error("Function not implemented."));
        // throw new Error("Function not implemented.");
    },
    resetFormData: function (): void {
        // console.log("resetFormData ---- ", new Error("Function not implemented."));
        // throw new Error("Function not implemented.");
    },
    focusedInput: null,
    setFocusedInput: function (name: string | null): void {
        // console.log("setFocusedInput ---- ", name, new Error("Function not implemented."));
        // throw new Error("Function not implemented.");
    },
    inputsRef: { current: {} }
});

export const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) {
        // console.log("useFormContext ---- ", new Error('useFormContext must be used within a FormProvider'));
        throw new Error('useFormContext must be used within a FormContextProvider');
    }
    return context;
};

// export const useFormContext = () => useContext(FormContext);

const FormContextProvider = ({ children }: T_Props) => {
    const [ctxFormData, setCtxFormData] = useState<I_FormData>({});
    const [ctxFormSchema, setCtxFormSchema] = useState<I_FormData>({});
    const [focusedInput, setFocusedInput] = useState<string | null>(null);
    const inputsRef = useRef<Record<string, any | null | undefined>>({});

    const updateCtxFormSchema = ({ formName, schema }: { formName: string, schema?: any }) => {
        // formName: it's form name as name suggested like contact | newsletter
        // name: input field name like firstName | lastName | email | contactNumber
        // value: input field's value like abc@example.com for email field
        setCtxFormSchema(prevState => ({
            ...prevState,
            [formName]: {
                ...prevState[formName],
                schema: {
                    ...prevState[formName]?.schema,
                    ...schema
                },
            }
        }));
    };

    const updateCtxFormData = ({ formName, name = "name", value = "", data }: { formName: string, name?: string, value?: any, data?: any }) => {
        // formName: it's form name as name suggested like contact | newsletter
        // name: input field name like firstName | lastName | email | contactNumber
        // value: input field's value like abc@example.com for email field
        // debugger;
        setCtxFormData(prevState => ({
            ...prevState,
            [formName]: {
                ...prevState[formName],
                data: {
                    ...prevState[formName]?.data,
                    ...(data ? data : { [name]: value })
                }
            }
        }));
    };

    const resetFormData = (formName: string = "contact") => {
        setCtxFormData(prevState => ({
            ...prevState,
            [formName]: {
                schema: prevState[formName].schema,
                data: {}
            }
        }));
    };

    // const focusInput = (name: string) => {
    //     if (inputsRef?.current?.[name]) {
    //       inputsRef?.current[name]?.focus();
    //     }
    //     setFocusedInput(name);
    // };

    const contextValue: I_FormContextType = {
        ctxFormData: ctxFormData,
        ctxFormSchema: ctxFormSchema,
        updateCtxFormSchema,
        updateCtxFormData,
        resetFormData,
        focusedInput,
        setFocusedInput,
        inputsRef
    };

    return (
        <FormContext.Provider value={contextValue}>
            {React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, inputsRef);
                }
                return child;
            })}
        </FormContext.Provider>
    );
}

export { FormContext, FormContextProvider };
